/* eslint-disable max-lines */
import { generateQueryKey } from '@lovoldsystem/core.client'
import { UserStatus } from './enums/user-status'
import { IDocumentCategoryRequestDto } from './generated-types'
import { formSchemaReportTypes } from './generated-types/form-schema'

export const serviceStationGroupQueryKey = {
  GET_SERVICE_STATION_GROUPS: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('service-station-group', { customerId, ...queryParams }),
  GET_SERVICE_STATION_GROUPS_FOR_AUTH: (customerId?: EntityId) =>
    generateQueryKey('service-station-group-for-auth', { customerId }),
  GET_SERVICE_STATIONS_FOR_GROUP: (customerId?: EntityId, id?: EntityId) =>
    generateQueryKey('service-station-group-service-stations', { customerId, id }),
}

export const calendarQueryKeys = {
  GET_GLOBAL_CALENDAR: (customerId?: EntityId, queryParams?: object) =>
    generateQueryKey('global-calendar', { customerId, ...queryParams }),
}

export const accessGroupQueryKey = {
  GET_LOCALITY_USERS: (customerId: EntityId, id?: EntityId) => generateQueryKey('locality-users', { customerId, id }),
  GET_STORAGE_USERS: (customerId: EntityId, id?: EntityId) => generateQueryKey('storage-users', { customerId, id }),
  GET_PROJECT_USERS: (customerId: EntityId, id: EntityId) => generateQueryKey('project-users', { customerId, id }),
  GET_SERVICE_STATION_GROUP_USERS: (customerId: EntityId, id: EntityId) =>
    generateQueryKey('service-station-group-users', { customerId, id }),
  GET_SERVICE_STATION_USERS: (customerId: EntityId, id?: EntityId) =>
    generateQueryKey('service-station-users', { customerId, id }),
  GET_FACILITIES: (queryParams?: any) => generateQueryKey('facilities', { ...queryParams }),
}

export const userQueryKey = {
  GET_USER_INVITE: (code?: EntityId) => generateQueryKey('user-invite', { code }),
  GET_SYSTEM_USERS: (searchKey?: string, status?: UserStatus) =>
    generateQueryKey('system-users', { searchKey, status }),
  GET_IDENTITY_SYNC_PREVIEW: () => generateQueryKey('identity-sync-preview'),
}

export const activityLogQueryKey = {
  GET_ACTIVITY_LOGS: (queryParams?: {
    positionId?: EntityId
    page?: number
    pageSize?: number
    mainComponentId?: EntityId
    fields?: string
  }) => {
    return generateQueryKey('get-activity-logs', queryParams)
  },
  GET_MAINTENANCE_LOGS: (queryParams?: { positionId?: EntityId; page?: number; pageSize?: number }) => {
    return generateQueryKey('get-maintenance-logs', queryParams)
  },
}

export const localityQueryKeys = {
  GET_LOCALITIES: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('localities', { customerId, ...queryParams }),
  GET_LOCALITIES_LOGO_NAME: (customerId?: EntityId) => generateQueryKey('localities-name-and-logo', { customerId }),
  GET_LOCALITY: (customerId?: EntityId, localityId?: EntityId, queryParams?: { mainComponentId?: EntityId }) =>
    generateQueryKey('locality', { customerId, localityId, ...queryParams }),
  GET_LOCALITY_WITH_ACTIVITY_COUNTS: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('locality-with-activity-counts', { customerId, localityId }),
  GET_CAGE_STATUS: (customerId?: EntityId, localityId?: EntityId, queryParams?: object) => {
    const params = { customerId, localityId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('cage-status', { customerId, localityId, ...queryParams })
  },
  GET_LOCALITY_DEVIATION_COUNT: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('deviation-count', { customerId, localityId }),
  GET_LOCALITY_WITH_INFO_COUNT: (customerId?: EntityId) => generateQueryKey('locality-with-info-count', { customerId }),
  GET_GROUPED_LOCALITY_WITH_INFO_COUNT: (customerId?: EntityId) =>
    generateQueryKey('grouped-locality-with-info-count', { customerId }),
  GET_LOCALITIES_WITH_MINIMAL_INFO: (customerId: EntityId) =>
    generateQueryKey('localities-with-minimal-info', { customerId }),
}

export const formSchemaQueryKeys = {
  GetFormSchemaReports: (queryParams?: {
    customerId?: EntityId
    type?: formSchemaReportTypes
    localityFrequencyIds?: Array<number | undefined>
    deviationId?: string
    mainComponentId?: string
    scheduledTaskId?: number
    localityId?: number
    activityLogId?: EntityId
  }) => generateQueryKey('form-schema-reports', { ...queryParams }),
  GET_FILLED_FORM_SCHEMA_REPORTS: (customerId?: EntityId, queryParams?: object) =>
    generateQueryKey('filled-form-schema-reports', { customerId, ...queryParams }),
  GET_FORM_SCHEMAS_FOR_MOVEMENT: (
    customerId?: EntityId,
    queryParams?: { equipmentTypeIds: number[]; movementTypes: number[] },
  ) => generateQueryKey('form-schemas-for-movement', { customerId, ...queryParams }),
  GET_FORM_SCHEMA_COLUMNS: (customerId?: EntityId, id?: EntityId) =>
    generateQueryKey('form-schema-columns', { customerId, id }),
  GET_CHECKLIST_STATUS: (customerId?: EntityId, scheduledTaskId?: number) =>
    generateQueryKey('checklist-status', { customerId, scheduledTaskId }),
}

export const localityFrequencyQueryKeys = {
  GET_LOCALITY_FREQUENCIES: (customerId?: EntityId, localityId?: EntityId, queryParams?: object) =>
    generateQueryKey('localityFrequencies', { customerId, localityId, ...queryParams }),
  GET_LOCALITY_FREQUENCY: (
    customerId?: EntityId,
    localityId?: EntityId,
    queryParams?: { id?: number | EntityId; groupId?: string },
  ) => generateQueryKey('locality-frequency', { customerId, localityId, ...queryParams }),
  GET_MOORING_SUMMARY: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('site-services-mooring-summary', { customerId, localityId }),
  GET_LOCALITY_SUMMARY: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('site-services-locality-summary', { customerId, localityId }),
}

export const DocumentCategoryKeys = {
  GET_DOCUMENT_CATEGORIES_FOR_DOCUMENTS: (customerId?: EntityId, queryParams?: IDocumentCategoryRequestDto) =>
    generateQueryKey('documentCategoriesForDocuments', { customerId, ...queryParams }),
}

export const CustomerDocumentCategoryKeys = {
  GET_CUSTOMER_AND_ADMIN_DOCUMENT_CATEGORIES: (customerId?: EntityId) =>
    generateQueryKey('getCustomerAndAdminDocumentCategories', { customerId }),
}

export const serviceStationQueryKey = {
  GET_USERS_FOR_SERVICE_STATION: (customerId?: EntityId, id?: EntityId) =>
    generateQueryKey('service-station-users', { customerId, id }),
  GET_SERVICE_STATION_GROUPS: (customerId?: EntityId, serviceStationId?: EntityId, queryParams?: object) =>
    generateQueryKey('service-station-groups', { customerId, serviceStationId, ...queryParams }),
  GET_COMPONENTS_BY_STATUS: (
    customerId?: EntityId,
    serviceStationId?: EntityId,
    status?: number,
    search?: string,
    selectedLanguage?: string,
  ) => generateQueryKey('components-by-status', { customerId, serviceStationId, status, search, selectedLanguage }),
}

export const customerQueryKey = {
  GET_CUSTOMER_USERS: (customerId?: EntityId, isWithClientUserLinks?: boolean) =>
    generateQueryKey('customer-users', { customerId, isWithClientUserLinks: isWithClientUserLinks as any }),
  GET_CUSTOMER_USERS_FOR_ADMINS: (
    customerId?: EntityId,
    isWithClientUserLinks?: boolean,
    searchText?: string,
    roleFilters?: number[],
  ) =>
    generateQueryKey('customer-users-for-admins', {
      customerId,
      isWithClientUserLinks: isWithClientUserLinks as any,
      searchText,
      roleFilters,
    }),
  GET_CUSTOMER: (customerId?: EntityId) => generateQueryKey('customer', { customerId }),
  GET_LOCALITIES_FOR_MAP: (searchValue?: string, selectedCustomer?: EntityId) =>
    generateQueryKey('localities-for-map', { searchValue, selectedCustomer }),
  GET_USER_STORAGE_CUSTOMERS: () => generateQueryKey('user-storage-customers'),
  GET_CUSTOMERS_FOR_EQUIPMENT_APPROVAL: (customerId?: EntityId, search?: string) =>
    generateQueryKey('customers-for-equipment-approval', { customerId, search }),
  GET_ACTIVITY_COUNTS_BATCH: (customerIds: EntityId[]) =>
    generateQueryKey('activity-counts-batch', { customerIds: customerIds?.join(',') }),
  GET_USER_ROLES: (customerId?: EntityId) => generateQueryKey('user-roles', { customerId }),
}

export const userInviteQueryKey = {
  GET_USER_INVITES: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('user-invites', { customerId, ...queryParams }),
}

export const positionQueryKey = {
  GET_Position_DEVIATION_COUNT: (customerId?: EntityId, localityId?: EntityId, positionId?: EntityId) => {
    const params = { customerId, localityId, positionId }
    removeNullParams(params)
    return generateQueryKey('position-deviation-count', params)
  },
  GET_POSITIONS: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId, queryParams?: object) => {
    const params = { customerId, localityId, mooringId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('get-positions', params)
  },
  GET_POSITIONS_MINIMAL_DATA: (
    customerId?: EntityId,
    localityId?: EntityId,
    mooringId?: EntityId,
    queryParams?: object,
  ) => {
    const params = { customerId, localityId, mooringId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('get-positions-minimal-data', params)
  },
  GET_POSITION: (
    customerId?: EntityId,
    localityId?: EntityId,
    mooringId?: EntityId,
    positionId?: EntityId,
    queryParams?: object,
  ) => {
    const params = { customerId, localityId, mooringId, positionId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('get-position', params)
  },
  GET_CAGE_SELECT_OPTION_BY_MOORING: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) => {
    const params = { customerId, localityId, mooringId }
    removeNullParams(params)
    return generateQueryKey('get-cage-select-options-by-mooring', params)
  },
  GET_POSITION_ROVVIDEO: (
    customerId?: EntityId,
    localityId?: EntityId,
    mooringId?: EntityId,
    positionId?: EntityId,
    queryParams?: object,
  ) => {
    const params = { customerId, localityId, mooringId, positionId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('get-position-rovvideo', params)
  },
  GET_POSITIONS_FOR_DROPDOWN_BY_MOORING: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) => {
    const params = { customerId, localityId, mooringId }
    removeNullParams(params)
    return generateQueryKey('get-positions-for-dropdown-by-mooring', params)
  },
  GET_POSITIONS_FOR_DROPDOWN_BY_LOCALITY: (customerId?: EntityId, localityId?: EntityId) => {
    const params = { customerId, localityId }
    removeNullParams(params)
    return generateQueryKey('get-positions-for-dropdown-by-locality', params)
  },
  GET_CAGES_FOR_DROPDOWN_BY_MOORING: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) => {
    const params = { customerId, localityId, mooringId }
    removeNullParams(params)
    return generateQueryKey('get-cages-for-dropdown-by-mooring', params)
  },
  GET_BATCH_NUMBER_DETAILS_BY_PRODUCT_ID: (
    customerId?: EntityId,
    localityId?: EntityId,
    mooringId?: EntityId,
    productId?: EntityId,
  ) => {
    const params = { customerId, localityId, mooringId, productId }
    removeNullParams(params)
    return generateQueryKey('get-batch-number-details-by-product', params)
  },
  GET_COMPONENTS_FOR_POSITIONS: (
    customerId: EntityId,
    localityId: EntityId,
    positionIds?: EntityId[],
    positionDrawingId?: EntityId,
  ) => {
    return generateQueryKey('get-components-for-positions', { customerId, localityId, positionIds, positionDrawingId })
  },
  GET_COMPONENTS_BY_PRODUCT_ID: (
    customerId: EntityId,
    localityId: EntityId,
    positionIds?: EntityId[],
    productId?: EntityId,
    positionNumber?: number,
  ) => {
    return generateQueryKey('get-position-components-by-productId', {
      customerId,
      localityId,
      positionIds,
      productId,
      positionNumber,
    })
  },
  GET_CONNECTED_POSITIONS_FOR_DROPDOWN: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) => {
    const params = { customerId, localityId, mooringId }
    removeNullParams(params)
    return generateQueryKey('get-connected-positions-for-dropdown', params)
  },
}

export const workAssignmentQueryKeys = {
  GET_WORK_ASSIGNMENTS: (customerId?: EntityId, localityId?: EntityId, queryParams?: any) =>
    generateQueryKey('work-assignments', { customerId, localityId, ...queryParams }),
  GET_WORK_ASSIGNMENT_REPORTS: (customerId?: EntityId, localityId?: EntityId, queryParams?: any) =>
    generateQueryKey('work-assignments-reports', { customerId, localityId, ...queryParams }),
  GET_WORK_ASSIGNMENT_POSITIONS: (
    customerId?: EntityId,
    localityId?: EntityId,
    queryParams?: { reportId?: number; mooringId?: EntityId },
  ) => generateQueryKey('work-assignment-mooring-position', { customerId, localityId, ...queryParams }),
  GET_WORK_ASSIGNMENT: (customerId?: EntityId, localityId?: EntityId, id?: EntityId) =>
    generateQueryKey('single-work-assignment', { customerId, localityId, id }),
  GET_WORK_ASSIGNMENT_BATCH_NUMBER_DETAILS: (
    customerId?: EntityId,
    localityId?: EntityId,
    workAssignmentId?: EntityId,
    productId?: EntityId,
  ) =>
    generateQueryKey('work-assignment-batch-number-details', { customerId, localityId, workAssignmentId, productId }),
  GET_WORK_ASSIGNMENT_COMPONENTS: (
    customerId: EntityId,
    localityId: EntityId,
    workAssignmentId: EntityId,
    workAssignmentPositionId?: EntityId,
  ) =>
    generateQueryKey('all-work-assignment-components', {
      customerId,
      localityId,
      workAssignmentId,
      workAssignmentPositionId,
    }),
  GET_POSITIONS_WITH_COMPONENTS: (customerId?: EntityId, localityId?: EntityId, workAssignmentId?: EntityId) =>
    generateQueryKey('positions-with-components', {
      customerId,
      localityId,
      workAssignmentId,
    }),
}

export const documentQueryKeys = {
  GET_DOCUMENT_EXPIRATION_TYPES: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('documentExpirationTypes', { customerId, localityId }),
  GET_DOCUMENTS_FOR_APPROVAL: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('documentsForApproval', { customerId, localityId }),
  GET_DOCUMENTS: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('documents', { customerId, ...queryParams }),
  GET_ARCHIVED_DOCUMENTS: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('archivedDocuments', { customerId, ...queryParams }),
}

export const mooringQueryKeys = {
  GET_MOORING: (customerId?: EntityId, localityId?: EntityId, id?: EntityId, queryParams?: any) =>
    generateQueryKey('get-mooring', { customerId, localityId, id, ...queryParams }),
  GET_MOORINGS_FOR_MAP: (customerId?: EntityId, localityId?: EntityId, queryParams?: any) =>
    generateQueryKey('mooringsForMap', { customerId, localityId, ...queryParams }),
  GET_MOORING_OUTLINES_FOR_MAP: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) =>
    generateQueryKey('mooringOutlinesForMap', { customerId, localityId, mooringId }),
  GET_SVG_FOR_POSITION: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId, queryParams?: any) =>
    generateQueryKey('svg-for-position', { customerId, localityId, mooringId, ...queryParams }),
  GET_MAP_SVG: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) =>
    generateQueryKey('svg-map', { customerId, localityId, mooringId }),
  GET_MOORING_FOR_DROPDOWN: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('mooring-for-dropdown', { customerId, localityId }),
  GET_ARCHIVED_MOORINGS: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('archived-moorings', { customerId, localityId }),
  GET_ALL_POSITION_GROUPS: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) =>
    generateQueryKey('position-groups', { customerId, localityId, mooringId }),
  GET_POSITION_GROUPS_WITH_DRAWING: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) =>
    generateQueryKey('position-groups-with-drawing', { customerId, localityId, mooringId }),
  GET_POSITION_GROUPS_FOR_DRAWING: (
    customerId?: EntityId,
    localityId?: EntityId,
    mooringId?: EntityId,
    positionDrawingId?: EntityId,
  ) => generateQueryKey('position-groups-for-drawing', { customerId, localityId, mooringId, positionDrawingId }),
  GET_EXTERNAL_COMPONENTS_FOR_MOORING: (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) =>
    generateQueryKey('external-components-for-mooring', { customerId, localityId, mooringId }),
}

export const mainComponentQueryKeys = {
  GET_MAIN_COMPONENTS: (customerId?: EntityId, queryParams?: any) => {
    const params = { customerId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('mainComponents', { customerId, ...params })
  },
  GET_MAIN_COMPONENT_BY_ID: (customerId: EntityId, id?: EntityId, queryParams?: object) =>
    generateQueryKey('mainComponentById', { customerId, id, ...queryParams }),
  GET_MAIN_COMPONENTS_LIST: (customerId?: EntityId, positionId?: EntityId) => {
    return generateQueryKey('mainComponentsList', { customerId, positionId })
  },
  GET_STORAGE_EQUIPMENT_LIST: (
    customerId?: EntityId,
    storage?: EntityId,
    search?: string,
    selectedLanguage?: string,
  ) => {
    return generateQueryKey('storageEquipmentList', { customerId, storage, search, selectedLanguage })
  },
  GET_EQUIPMENT: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('equipment', { customerId, ...queryParams }),
  GET_EQUIPMENT_TYPES: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('equipment-types', { customerId, ...queryParams }),
  GET_MAINTENANCE_PERFORM_HISTORY: (customerId?: EntityId, id?: EntityId, queryParams?: object) =>
    generateQueryKey('mainComponent-perform-history', { customerId, id, ...queryParams }),
  GET_BY_SEARCH: (customerId?: EntityId, searchText?: string, queryParams?: object) =>
    generateQueryKey('getBySearch', { customerId, searchText, ...queryParams }),
  GET_MAIN_COMPONENT_ROV_VIDEOS: (customerId?: EntityId, id?: EntityId) =>
    generateQueryKey('mainComponent-rov-videos', { customerId, id }),
  GET_BY_SEARCH_WITH_FILTERS: (
    customerId?: EntityId,
    searchText?: string,
    fieldFilters?: object,
    page?: number,
    pageSize?: number,
  ) => generateQueryKey('getBySearchWithFilters', { customerId, searchText, ...fieldFilters, page, pageSize }),
  GET_EQUIPMENTS_WITH_LOCALITY_FILTER: (customerId?: EntityId, queryParams?: object) =>
    generateQueryKey('equipmentsWithLocalityFilter', { customerId, ...queryParams }),
  GET_MAIN_COMPONENTS_BY_COMPONENT_TYPE: (customerId?: EntityId, componentTypeId?: EntityId, searchText?: string) =>
    generateQueryKey('mainComponentsByComponentType', { customerId, componentTypeId, searchText }),
  GET_MAIN_COMPONENTS_BY_IDS: (customerId?: EntityId, ids?: EntityId[]) =>
    generateQueryKey('mainComponentsByIds', { customerId, ids: ids?.join(',') }),
}

export const deviationQueryKeys = {
  GET_WEEKLY_COUNT: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('deviation-weeklyCount', { customerId, localityId }),
  GET_ADMIN_DEVIATIONS: (customerId?: EntityId) => generateQueryKey('admin-deviations', { customerId }),
  GET_ADMIN_DEVIATION: (id?: EntityId) => generateQueryKey('admin-deviation', { id }),
  GET_DEVIATION: (customerId?: EntityId, id?: EntityId) => generateQueryKey('deviation', { customerId, id }),
  GET_DEVIATIONS: (customerId?: EntityId, queryParams?: object) =>
    generateQueryKey('deviations', { customerId, ...queryParams }),
  GET_DEVIATION_DOCUMENTS: (customerId?: EntityId, id?: EntityId) =>
    generateQueryKey('deviation-documents', { customerId, id }),
  GET_ADMIN_DEVIATIONS_LIST: (customerId?: EntityId, queryParams?: object) =>
    generateQueryKey('admin-deviations-list', { customerId, ...queryParams }),
  GET_ADMIN_DEVIATION_STATS: (customerId?: EntityId, queryParams?: object) =>
    generateQueryKey('admin-deviation-stats', { customerId, ...queryParams }),
  GET_ADMIN_DEVIATION_DETAIL: (customerId?: EntityId, id?: EntityId) =>
    generateQueryKey('admin-deviation-detail', { customerId, id }),
  GET_DEVIATIONS_FOR_SCHEDULED_WORK_ORDERS: (
    customerId?: EntityId,
    localityId?: EntityId,
    scheduledTaskId?: EntityId,
  ) => generateQueryKey('deviations-scheduled-work-orders', { customerId, localityId, scheduledTaskId }),
  GET_DEVIATIONS_BY_MAIN_COMPONENT_IDS: (customerId?: EntityId, mainComponentIds?: EntityId[], status?: boolean) =>
    generateQueryKey('deviations-by-main-component-ids', {
      customerId,
      ids: mainComponentIds?.join(','),
      status: status?.toString(),
    }),
}

export const taskQueryKeys = {
  GET_ALL_SCHEDULED_TASKS: (localityId?: EntityId, queryParams?: object) => {
    const params = { localityId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('get-all-scheduled-tasks', params)
  },
  GET_SCHEDULED_TASK: (localityId?: EntityId, taskId?: EntityId, queryParams?: object) =>
    generateQueryKey('get-scheduled_Task', { localityId, taskId, ...queryParams }),
  GET_CALENDAR_SCHEDULE_TASK: (customerId?: EntityId, queryParams?: object) => {
    const params = { customerId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('get-calendar-schedule-task', params)
  },
  GET_SCHEDULED_TASKS: (localityId?: EntityId, queryParams?: object) => {
    const params = { localityId, ...queryParams }
    removeNullParams(params)
    return generateQueryKey('get-scheduled-tasks', params)
  },
  GET_EQUIPMENT_FOR_WORK_ORDER: (localityId?: EntityId, scheduledTaskId?: EntityId) => {
    return generateQueryKey('get-equipment-for-work-order', { localityId, scheduledTaskId })
  },
  GET_EQUIPMENT_OF_SCHEDULED_WORK_ORDER: (localityId?: EntityId, scheduledTaskId?: EntityId) => {
    return generateQueryKey('get-equipment-of-scheduled-work-order', { localityId, scheduledTaskId })
  },
  GET_WORK_ORDER_ATTACHMENTS: (localityId?: EntityId, scheduledTaskId?: EntityId) => {
    return generateQueryKey('get-work-order-attachments', { localityId, scheduledTaskId })
  },
}

const removeNullParams = (obj: Record<string, any>): Record<string, any> => {
  Object.keys(obj).forEach(key => {
    if (obj[key] === null) {
      delete obj[key]
    }
  })
  return obj
}

export const scheduledMaintenanceQueryKeys = {
  GET_SCHEDULED_MAINTENANCE_BY_ID: (customerId?: EntityId, localityId?: EntityId, scheduledMaintenanceId?: EntityId) =>
    generateQueryKey('scheduled-maintenance-by-id', { customerId, localityId, scheduledMaintenanceId }),
  GET_SCHEDULED_MAINTENANCES_BY_CAGE: (
    customerId?: EntityId,
    localityId?: EntityId,
    cagePositionId?: EntityId,
    queryParams?: object,
  ) => generateQueryKey('scheduled-maintenances-by-cage', { customerId, localityId, cagePositionId, ...queryParams }),
  GET_SCHEDULED_MAINTENANCES_BY_EQUIPMENT: (
    customerId?: EntityId,
    localityId?: EntityId,
    mainComponentTypeId?: EntityId,
    queryParams?: object,
  ) =>
    generateQueryKey('scheduled-maintenances-by-equipment', {
      customerId,
      localityId,
      mainComponentTypeId,
      ...queryParams,
    }),
  GET_LOCALITY_SCHEDULED_MAINTENANCES: (customerId?: EntityId, localityId?: EntityId, queryParams?: object) =>
    generateQueryKey('locality-scheduled-maintenances', { customerId, localityId, ...queryParams }),
  GET_CAGE_POSITIONS: (customerId?: EntityId, localityId?: EntityId, queryParams?: object) =>
    generateQueryKey('cage-positions', { customerId, localityId, ...queryParams }),
  GET_MAIN_COMPONENT_TYPES: (customerId?: EntityId, localityId?: EntityId, queryParams?: object) =>
    generateQueryKey('main-component-types', { customerId, localityId, ...queryParams }),
  GET_EQUIPMENT_PERFORMED_MAINTENANCES: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('equipment-performed-maintenances', { customerId, localityId }),
  GET_LOCALITY_PERFORMED_MAINTENANCES: (customerId?: EntityId, localityId?: EntityId) =>
    generateQueryKey('locality-performed-maintenances', { customerId, localityId }),
  GET_PERFORMABLE_SCHEDULED_MAINTENANCES: (customerId?: EntityId, localityId?: EntityId, ids?: number[]) =>
    generateQueryKey('performable-scheduled-maintenances', { customerId, localityId, ids }),
  GET_MAINTENANCE_LIST: (customerId?: EntityId, localityId?: EntityId, queryParams?: object) =>
    generateQueryKey('maintenance-list', { customerId, localityId, ...queryParams }),
}

export const projectQueryKeys = {
  GET_PROJECTS: (customerId: EntityId) => generateQueryKey('projects-by-customer', { customerId }),
  GET_PROJECT: (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
    generateQueryKey('project-by-id', { customerId, localityId, projectId }),
  GET_PROJECT_VERSIONS: (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
    generateQueryKey('project-versions', { customerId, localityId, projectId }),
}

export const projectPositionQueryKeys = {
  GET_POSITION_NAMES: (customerId: EntityId, projectId: EntityId) => {
    return generateQueryKey('project-position-names', { customerId, projectId })
  },
  GET_POSITIONS: (customerId: EntityId, projectId: EntityId, searchInput?: string) => {
    return generateQueryKey('project-positions-by-customer', { customerId, projectId, searchInput })
  },
  GET_POSITIONS_FOR_PROJECT: (customerId: EntityId, projectId: EntityId) => {
    return generateQueryKey('project-positions-by-customer', { customerId, projectId })
  },
  GET_COMPONENTS_FOR_POSITIONS: (customerId: EntityId, projectId: EntityId, positionIds?: EntityId[]) => {
    return generateQueryKey('project-position-components', { customerId, projectId, positionIds })
  },
  // Nested under GET_COMPONENTS_FOR_POSITIONS so invalidating the group's components also refreshes these
  GET_COMPONENTS_FOR_SELECTED_POSITIONS: (
    customerId: EntityId,
    projectId: EntityId,
    groupPositionIds: EntityId[],
    selectedPositionIds: EntityId[],
  ) => {
    return generateQueryKey('project-position-components', {
      customerId,
      projectId,
      groupPositionIds,
      selectedPositionIds,
    })
  },
  GET_WORK_ITEMS_FOR_POSITION: (customerId: EntityId, projectId: EntityId, positionId: EntityId) => {
    return generateQueryKey('project-position-work-items', { customerId, projectId, positionId })
  },
  GET_POSITION: (customerId: EntityId, projectId: EntityId, positionId: EntityId) => {
    return generateQueryKey('project-position-by-id', { customerId, projectId, positionId })
  },
  GET_MULTIPLE_POSITIONS(customerId: EntityId, projectId: EntityId, positionIds: EntityId[]) {
    return generateQueryKey('project-multiple-positions', { customerId, projectId, positionIds })
  },
  GET_COMPONENTS_BY_PRODUCT_ID: (
    customerId: EntityId,
    projectId: EntityId,
    positionIds: EntityId[],
    productId?: EntityId,
    positionNumber?: number,
  ) => {
    return generateQueryKey('project-position-components-by-productId', {
      customerId,
      projectId,
      positionIds,
      productId,
      positionNumber,
    })
  },
  GET_POSITION_DRAWING_GROUPS: (customerId: EntityId, projectId: EntityId, positionDrawingId?: EntityId) => {
    return generateQueryKey('project-position-drawing-groups', { customerId, projectId, positionDrawingId })
  },
  GET_ALL_POSITION_DRAWING_GROUPS: (customerId: EntityId, projectId: EntityId) => {
    return generateQueryKey('project-all-position-drawing-groups', { customerId, projectId })
  },
  GET_POSITIONS_GROUPED_BY_DRAWING_GROUPS: (customerId: EntityId, projectId: EntityId) => {
    return generateQueryKey('project-positions-grouped-by-drawing-groups', { customerId, projectId })
  },
}
export const maintenanceQueryKeys = {
  GET_LOCALITY_MAINTENANCES: (customerId?: EntityId, localityId?: EntityId, queryParams?: any) =>
    generateQueryKey('locality-maintenances', { customerId, localityId, ...queryParams }),
  GET_ALL_MAINTENANCES: (customerId?: EntityId, localityId?: EntityId, queryParams?: any) =>
    generateQueryKey('maintenances', { customerId, localityId, ...queryParams }),
  GET_MAINTENANCES_BY_CAGE: (
    customerId?: EntityId,
    localityId?: EntityId,
    cagePositionId?: EntityId,
    queryParams?: object,
  ) => generateQueryKey('maintenances-by-cage', { customerId, localityId, cagePositionId, ...queryParams }),
  GET_MAINTENANCES_BY_EQUIPMENT_TYPE: (
    customerId?: EntityId,
    localityId?: EntityId,
    mainComponentTypeId?: EntityId,
    queryParams?: object,
  ) =>
    generateQueryKey('maintenances-by-equipmentType', { customerId, localityId, mainComponentTypeId, ...queryParams }),
  GET_SCHEDULED_MAINTENANCES_BY_MAINTENANCE: (
    customerId?: EntityId,
    localityId?: EntityId,
    maintenanceId?: EntityId,
    queryParams?: object,
  ) =>
    generateQueryKey('scheduled-maintenances-by-maintenance', {
      customerId,
      localityId,
      maintenanceId,
      ...queryParams,
    }),
  GET_EQUIPMENT_BY_MAINTENANCE: (
    customerId?: EntityId,
    localityId?: EntityId,
    maintenanceId?: EntityId,
    positionId?: EntityId,
    equipmentTypeId?: EntityId,
  ) =>
    generateQueryKey('equipment-by-maintenance', {
      customerId,
      localityId,
      maintenanceId,
      positionId,
      equipmentTypeId,
    }),
}

export const componentQueryKeys = {
  GET_COMPONENTS: (customerId?: EntityId, positionId?: EntityId, componentId?: EntityId) =>
    generateQueryKey('components', { customerId, positionId, componentId }),
}

export const productQueryKeys = {
  GET_PRODUCTS: (searchInput?: string, queryParams?: object) =>
    generateQueryKey('products', { searchInput, ...queryParams }),
}

export const resourceQueryKeys = {
  GET_RESOURCES: (customerId?: EntityId, queryParams?: any) =>
    generateQueryKey('resources', { customerId, ...queryParams }),
  GET_RELATED_TASKS: (customerId: EntityId, id?: EntityId) => generateQueryKey('related-tasks', { customerId, id }),
}
export const impregnationQueryKeys = {
  GET_IMPREGNATIONS: (equipmentId?: EntityId, queryParams?: object) =>
    generateQueryKey('impregnations', { equipmentId, ...queryParams }),
}

export const washingReportQueryKeys = {
  GET_WASHING_REPORTS: (equipmentId?: EntityId, queryParams?: object) =>
    generateQueryKey('washing-reports', { equipmentId, ...queryParams }),
}

export const mainComponentEventQueryKeys = {
  GET_MAIN_COMPONENT_EVENTS: (mainComponentId?: EntityId, queryParams?: object) =>
    generateQueryKey('main-component-events', { mainComponentId, ...queryParams }),
}

export const apiKeysQueryKeys = {
  GET_API_KEYS: (customerId?: EntityId) => generateQueryKey('api-keys', { customerId }),
}

export const newsQueryKeys = {
  GET_NEWS_FEED: (customerId?: EntityId, params?: any) => generateQueryKey('news-feed', { customerId, ...params }),
  GET_CUSTOMER_NEWS: (customerId?: EntityId, params?: any) =>
    generateQueryKey('customer-news', { customerId, ...params }),
  GET_NEWS_ARTICLE: (customerId?: EntityId, id?: EntityId) => generateQueryKey('news-article', { customerId, id }),
  GET_ADMIN_NEWS: (params?: any) => generateQueryKey('admin-news', { ...params }),
  GET_ADMIN_NEWS_ARTICLE: (id?: EntityId) => generateQueryKey('admin-news-article', { id }),
}

export const appFeatureQueryKeys = {
  GET_APP_FEATURE_MAP: (customerIds?: EntityId[]) =>
    generateQueryKey('app-feature-map', { customerIds: customerIds?.join(',') }),
  GET_ADMIN_APP_FEATURES: () => generateQueryKey('admin-app-features'),
  GET_CUSTOMER_APP_FEATURES: (customerId?: EntityId) => generateQueryKey('customer-app-features', { customerId }),
  GET_FEATURE_CUSTOMERS: (featureId?: EntityId) => generateQueryKey('app-feature-customers', { featureId }),
}

export const productCertificateQueryKeys = {
  GET_PRODUCT_CERTIFICATES: (customerId?: EntityId, queryParams?: object) =>
    generateQueryKey('product-certificates', { customerId, ...queryParams }),
}

export const reportTemplateQueryKeys = {
  GET_FORM_SCHEMA_REPORT_TEMPLATES: (customerId?: EntityId, queryOptions?: any) =>
    generateQueryKey('form-schema-report-templates', { customerId, ...queryOptions }),
  GET_REPORT_TEMPLATE_BY_ID: (customerId?: EntityId, id?: EntityId) =>
    generateQueryKey('report-template-by-id', { customerId, id }),
  GET_FORM_SCHEMA_REPORT_TEMPLATE_COUNTS: (customerId?: EntityId) =>
    generateQueryKey('form-schema-report-template-counts', { customerId }),
  GET_REPORT_TEMPLATES_BY_FORM_SCHEMA_VERSION: (customerId?: EntityId, formSchemaVersionId?: EntityId) =>
    generateQueryKey('report-templates-by-form-schema-version', { customerId, formSchemaVersionId }),
  GET_REPORT_TEMPLATE_WARNINGS: (customerId?: EntityId, reportTemplateId?: EntityId) =>
    generateQueryKey('report-template-warnings', { customerId, reportTemplateId }),
}
